/**
 * <splide-slideshow> — image-only slideshow powered by Splide.
 *
 * Reads Splide options from data-* attributes (set from section settings) and
 * mounts Splide on the inner `.splide` element. Integrates with the Shopify
 * theme editor (section reload + block select/deselect).
 */
if (!customElements.get('splide-slideshow')) {
  class SplideSlideshow extends HTMLElement {
    connectedCallback() {
      // Deferred scripts run in order, so Splide is normally defined by now.
      // Guard anyway in case of unusual load orders.
      if (typeof Splide === 'undefined') {
        window.addEventListener('load', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      const root = this.querySelector('.splide');
      if (!root || this.splide || typeof Splide === 'undefined') return;
      if (root.querySelectorAll('.splide__slide').length === 0) return;

      this.splide = new Splide(root, this.options());
      // Assemble arrows + pagination into one bottom-centered bar (design spec).
      this.splide.on('mounted refresh', () => this.buildNav(root));
      this.splide.mount();
    }

    /**
     * Move the prev arrow, pagination, and next arrow into a single centered
     * control bar: ‹  • ▬▬ •  ›. Re-parenting keeps Splide's own listeners and
     * active-state syncing intact. Idempotent — safe to call on every refresh.
     */
    buildNav(root) {
      const prev = root.querySelector('.splide__arrow--prev');
      const next = root.querySelector('.splide__arrow--next');
      const pagination = root.querySelector('.splide__pagination');
      if (!prev && !next && !pagination) return;

      let bar = root.querySelector(':scope > .splide-slideshow__nav');
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'splide-slideshow__nav';
        root.appendChild(bar);
      }
      if (prev) bar.appendChild(prev);
      if (pagination) bar.appendChild(pagination);
      if (next) bar.appendChild(next);
    }

    destroy() {
      if (this.splide) {
        this.splide.destroy(true);
        this.splide = null;
      }
    }

    options() {
      const data = this.dataset;
      const bool = (v, fallback) => (v === undefined ? fallback : v === 'true');
      const num = (v, fallback) => {
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? fallback : n;
      };

      const fade = data.transition === 'fade';
      const loop = bool(data.loop, true);

      const options = {
        type: fade ? 'fade' : loop ? 'loop' : 'slide',
        rewind: fade ? true : !loop,
        speed: num(data.speed, 600),
        autoplay: bool(data.autoplay, true),
        interval: num(data.interval, 5000),
        pauseOnHover: bool(data.pauseOnHover, true),
        pauseOnFocus: true,
        arrows: bool(data.arrows, true),
        pagination: bool(data.pagination, true),
        perPage: 1,
        perMove: 1,
        drag: true,
        keyboard: 'focused',
        i18n: { carousel: 'Slideshow' },
      };

      // Respect users who prefer reduced motion: disable autoplay.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        options.autoplay = false;
      }

      return options;
    }

    /* ---- Theme editor helpers ---- */

    goToBlock(blockId) {
      if (!this.splide) return;
      const slide = this.querySelector(`#${CSS.escape(blockId)}`);
      if (!slide) return;
      const index = parseInt(slide.dataset.slideIndex, 10);
      if (!Number.isNaN(index)) {
        this.splide.go(index);
      }
      this.pauseAutoplay();
    }

    pauseAutoplay() {
      const autoplay = this.splide && this.splide.Components.Autoplay;
      if (autoplay) autoplay.pause();
    }

    resumeAutoplay() {
      const autoplay = this.splide && this.splide.Components.Autoplay;
      if (autoplay && this.dataset.autoplay === 'true') autoplay.play();
    }
  }

  customElements.define('splide-slideshow', SplideSlideshow);

  /* ---- Shopify theme editor events ---- */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    const closestSlideshow = (id) =>
      document.querySelector(`#shopify-section-${id} splide-slideshow`);

    document.addEventListener('shopify:section:load', (event) => {
      const el = closestSlideshow(event.detail.sectionId);
      if (el) el.init();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const el = closestSlideshow(event.detail.sectionId);
      if (el) el.destroy();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const el = event.target.closest('splide-slideshow');
      if (el) el.goToBlock(event.detail.blockId);
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const el = event.target.closest('splide-slideshow');
      if (el) el.resumeAutoplay();
    });
  }
}
